// src/providers/AudioProvider.tsx
import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import {
  SAMPLE_RATE,
  MAX_CHARACTERS,
  TRANSCRIPTION_CLEAR_TIMER_MS,
} from "../utils/constants";
import { isiOS, isChrome } from "../utils/browserUtils";
import {
  ensureMediaSessionMetadata,
  disconnectMediaSession,
} from "../utils/mediaSessionUtils";
import { isHandsFreeModeSupported } from "../utils/moonshineUtils";
import { useHandleCompareRequest } from "../hooks/useHandleCompareRequest";
import { useMessages } from "./MessagesContext";
import { useSettings } from "./SettingsContext";

// Simple function to create a silent audio blob for audio trust initialization
const createSilentAudioBlob = (): Blob => {
  const audioContext = new (window.AudioContext ||
    (window as any).webkitAudioContext)();
  const sampleRate = audioContext.sampleRate;
  const length = sampleRate * 0.1; // 100ms of silence
  const buffer = audioContext.createBuffer(1, length, sampleRate);
  return new Blob([buffer.getChannelData(0)], { type: "audio/wav" });
};

// === Voice Message Type ===
type VoiceMsg =
  | {
      type: "status";
      status: "ready";
      message: string;
      duration?: string;
    }
  | {
      type: "output";
      message: string;
      start: number;
      end: number;
      duration: number;
    }
  | { type: "command"; command: string; arg?: any }
  | { type: "error"; message: string };

// === MEDIA STATE TYPES ===
type MediaStatus = "idle" | "loading" | "playing" | "paused" | "ended";
type MediaState = {
  currentId: string | null;
  status: MediaStatus;
  progress: number;
  error?: string;
};

type MediaStateStore = {
  getState: () => MediaState;
  subscribe: (listener: () => void) => () => void;
  select: (id: string) => {
    isCurrent: boolean;
    status: MediaStatus;
    progress: number;
  };
  setState: (patch: Partial<MediaState>) => void;
};

interface AudioProvider {
  // Makes Blobs from Audio and Videos playable with blob URLs and ids + prevents Chrome from GCing them
  registerAudioSource: (id: string, audioBlob: Blob) => void;
  unregisterAudioSource: (id: string) => void;
  registerVideoSource: (id: string, el: HTMLMediaElement) => void;
  unregisterVideoSource: (id: string) => void;

  // Media Playback
  play: (id: string) => Promise<void>;
  pause: (id: string) => void;
  setVolume: (volume: number) => void;

  // Voice Recording
  initializeVoice: () => Promise<void>;
  startListening: () => Promise<void>;
  stopListening: () => void;
  isListening: boolean;
  transcript: string;
  clearTranscript: () => void;
  sendTranscript: () => void;

  // Conversation Mode
  conversationMode: boolean;
  setConversationMode: (enabled: boolean) => void;

  // Play Responses Out Loud
  playResponseOutloud: boolean;
  setPlayResponseOutloud: (enabled: boolean) => void;

  // Audio Trust & iOS
  initializeAudioTrust: () => Promise<boolean>;
  isAudioTrustInitialized: boolean;

  isModelLoaded: boolean;
  status: string | null;
  error: string | null;

  // Media State Store
  mediaState: MediaStateStore;

  // Global video control
  fullscreenVideoMessageId: string | null;
  setFullscreenVideoMessageId: (messageId: string | null) => void;
  onVideoEnd: (messageId: string) => void;
}

// === CONTEXT ===
const AudioContext = createContext<AudioProvider | null>(null);
export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) throw new Error("useAudio must be used within AudioProvider");
  return context;
};

// === PROVIDER COMPONENT ===
export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // === SINGLE AUDIO CONTEXT ===
  const audioContextRef = useRef<AudioContext | null>(null);
  const { getLastVideoMessageId } = useMessages();
  const { settings } = useSettings();

  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const audioSourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const [audioKey, setAudioKey] = useState(0); // For forcing audio element remount

  // === MEDIA PLAYBACK ===
  const audioSources = useRef<Record<string, string>>({}); // id -> blob URL
  const videoElements = useRef<Record<string, HTMLMediaElement>>({});

  // === VOICE RECORDING ===
  const workerRef = useRef<Worker | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const mediaStreamSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  // === STATE ===
  const currentPlayingRef = useRef<string | null>(null);
  const [conversationMode, setConversationMode] = useState(false);
  const [playResponseOutloud, setPlayResponseOutloud] = useState(false);
  const [originalPlayResponseOutloud, setOriginalPlayResponseOutloud] =
    useState<boolean | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const transcriptRef = useRef(transcript);
  const transcriptUpdatePromiseRef = useRef<Promise<void> | null>(null);
  const transcriptTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    transcriptRef.current = transcript;
    // Resolve any pending transcript update promise
    if (transcriptUpdatePromiseRef.current) {
      transcriptUpdatePromiseRef.current = null;
    }
  }, [transcript]);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isAudioTrustInitialized, setIsAudioTrustInitialized] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isContextInitializingRef = useRef(false);
  const isStartingListeningRef = useRef(false);
  const audioContextInitPromiseRef = useRef<Promise<AudioContext> | null>(null);

  // === GLOBAL VIDEO CONTROL ===
  const [fullscreenVideoMessageId, setFullscreenVideoMessageId] = useState<
    string | null
  >(null);

  // === MEDIA STATE STORE ===
  const stateListeners = useRef(new Set<() => void>());
  const stateRef = useRef<MediaState>({
    currentId: null,
    status: "idle",
    progress: 0,
  });

  const notifyStateListeners = useCallback(() => {
    stateListeners.current.forEach((listener) => listener());
  }, []);

  const mediaStateStore: MediaStateStore = {
    getState: () => stateRef.current,
    subscribe: (listener: () => void) => {
      stateListeners.current.add(listener);
      return () => stateListeners.current.delete(listener);
    },
    select: (id: string) => {
      const state = stateRef.current;
      const isCurrent = state.currentId === id;
      return {
        isCurrent,
        status: isCurrent ? state.status : "idle",
        progress: isCurrent ? state.progress : 0,
      };
    },
    setState: (patch: Partial<MediaState>) => {
      stateRef.current = { ...stateRef.current, ...patch };
      notifyStateListeners();
    },
  };

  // === CORE AUDIO CONTEXT MANAGEMENT ===
  const ensureAudioContextRunning = useCallback(async () => {
    if (!audioContextRef.current) {
      throw new Error("AudioContext is not initialized");
    }

    if (audioContextRef.current.state === "running") {
      return; // Already running, nothing to do
    }

    const currentState = audioContextRef.current.state;

    if (currentState === "closed") {
      destroyAudioContext();
      throw new Error(
        `AudioContext is closed, recreating on next user gesture.`
      );
    } else if (currentState === "suspended") {
      await audioContextRef.current!.resume();
      const resumedState = audioContextRef.current!.state;
      if (resumedState === "suspended") {
        throw new Error(
          `Failed to resume AudioContext in running state. Current state: ${resumedState}`
        );
      }
    }
  }, []);

  const initializeAudioContext = useCallback((): Promise<AudioContext> => {
    // If already initialized, return the existing context
    if (audioContextRef.current) {
      return Promise.resolve(audioContextRef.current);
    }

    // If already initializing, return the existing promise
    if (audioContextInitPromiseRef.current) {
      return audioContextInitPromiseRef.current;
    }

    // Create new initialization promise
    isContextInitializingRef.current = true;

    audioContextInitPromiseRef.current = new Promise<AudioContext>(
      (resolve, reject) => {
        try {
          const AudioContextClass =
            window.AudioContext || (window as any).webkitAudioContext;
          audioContextRef.current = new AudioContextClass({
            sampleRate: SAMPLE_RATE,
            latencyHint: "interactive",
          });

          // Create gain node for volume control
          gainNodeRef.current = audioContextRef.current.createGain();

          // Apply volume from settings
          const volume = settings.volume;
          let gain: number;
          if (volume <= 50) {
            gain = volume / 50; // 0-50% maps to 0-1 gain
          } else {
            gain = 1 + ((volume - 50) / 50) * 9; // 50-100% maps to 1-10 gain
          }
          gainNodeRef.current.gain.value = gain;

          // Create audio source node for volume control
          if (audioElementRef.current) {
            audioSourceNodeRef.current =
              audioContextRef.current.createMediaElementSource(
                audioElementRef.current
              );
            audioSourceNodeRef.current.connect(gainNodeRef.current);
            gainNodeRef.current.connect(audioContextRef.current.destination);
          } else {
            console.warn(
              "[AudioProvider] Audio element not ready, will connect later"
            );
            // Connect gain node directly to destination for now
            gainNodeRef.current.connect(audioContextRef.current.destination);
          }

          if (isiOS() && navigator.audioSession) {
            navigator.audioSession.type = "transient";
            setTimeout(() => {
              navigator.audioSession!.type = "playback";
            }, 200);

            // play a silent audio blob through the shared audio element to kick audio routing to work
            const silentAudioBlob = createSilentAudioBlob();
            audioElementRef.current!.src = URL.createObjectURL(silentAudioBlob);
            audioElementRef.current!.load();
            audioElementRef.current!.play();
          }

          isContextInitializingRef.current = false;
          resolve(audioContextRef.current);
        } catch (error) {
          console.error(
            "[AudioProvider] Failed to initialize AudioContext:",
            error
          );
          isContextInitializingRef.current = false;
          audioContextInitPromiseRef.current = null;
          reject(error);
        }
      }
    );

    return audioContextInitPromiseRef.current;
  }, [settings.volume]);

  const destroyAudioContext = useCallback(() => {
    // Clean up existing context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    // make sure gain and source nodes are disconnected
    if (gainNodeRef.current) {
      gainNodeRef.current.disconnect();
      gainNodeRef.current = null;
    }
    if (audioSourceNodeRef.current) {
      audioSourceNodeRef.current.disconnect();
      audioSourceNodeRef.current = null;
    }

    // Clean up voice recording resources
    if (mediaStreamSourceRef.current) {
      mediaStreamSourceRef.current.disconnect();
      mediaStreamSourceRef.current = null;
    }
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    // Reset state
    setIsListening(false);
    setIsAudioTrustInitialized(false);
    isContextInitializingRef.current = false;
    audioContextInitPromiseRef.current = null;

    // Force audio element remount - this will trigger the useEffect to recreate the context
    setAudioKey((prev) => prev + 1);
  }, []);

  // === CONVERSATION MODE HANDLING ===
  const handleConversationModeToggle = useCallback(
    async (enabled: boolean) => {
      if (!enabled) {
        cleanUpConversationMode();
        return;
      }

      if (!isHandsFreeModeSupported()) {
        // Show notification for unsupported browser
        alert("Hands-free mode is not supported in your browser.");
        return;
      }
      setConversationMode(enabled);
      setOriginalPlayResponseOutloud(playResponseOutloud);
      setPlayResponseOutloud(true);

      // Hide keyboard if it's open
      if (
        document.activeElement &&
        document.activeElement instanceof HTMLElement
      ) {
        document.activeElement.blur();
      }

      // Start Voice ASR with Moonshine
      setTimeout(async () => {
        try {
          await startListening();
        } catch (error) {
          console.error("Failed to start Voice ASR:", error);
          // Optionally show user-friendly error message
          alert("Failed to start voice recognition. Please try again.");
          setConversationMode(false);
        }
      }, 750);
    },
    [
      isListening,
      destroyAudioContext,
      playResponseOutloud,
      originalPlayResponseOutloud,
    ]
  );

  // === MEDIA PLAYBACK METHODS ===
  // UseCallBack prevents the function from being recreated on every render since these are passed down to child components
  const registerAudioSource = useCallback((id: string, audioBlob: Blob) => {
    if (audioSources.current[id]) URL.revokeObjectURL(audioSources.current[id]); // clean up existing URL
    audioSources.current[id] = URL.createObjectURL(audioBlob); // Create new blob URL
  }, []);

  const unregisterAudioSource = useCallback(
    (id: string) => {
      if (audioSources.current[id]) {
        if (currentPlayingRef.current === id) {
          pause(id);
          currentPlayingRef.current = null;
          mediaStateStore.setState({
            currentId: null,
            status: "idle",
            progress: 0,
          });
        }
        URL.revokeObjectURL(audioSources.current[id]);
        delete audioSources.current[id];
      }
    },
    [mediaStateStore]
  );

  // === VIDEO REGISTRATION METHODS (for compatibility with MediaDisplay) ===
  const registerVideoSource = useCallback(
    (id: string, el: HTMLMediaElement) => {
      const videoId = id.startsWith("vid-") ? id : `vid-${id}`;
      videoElements.current[videoId] = el;
    },
    [isAudioTrustInitialized]
  );

  const unregisterVideoSource = useCallback(
    (id: string) => {
      const videoId = id.startsWith("vid-") ? id : `vid-${id}`;
      delete videoElements.current[videoId];

      // If this was the currently playing video, stop it
      if (currentPlayingRef.current === videoId) {
        currentPlayingRef.current = null;
        mediaStateStore.setState({
          currentId: null,
          status: "idle",
          progress: 0,
        });
      }
    },
    [mediaStateStore]
  );

  const play = useCallback(async (id: string) => {
    // Ensure audio context is initialized
    let ctx = audioContextRef.current;
    if (!ctx) {
      try {
        ctx = await initializeAudioContext();
      } catch (error) {
        console.error(
          "[AudioProvider] Failed to initialize audio context for play:",
          error
        );
        return;
      }
    }

    // Ensure MediaSession API is properly configured for iOS Safari before playing
    if (isiOS()) ensureMediaSessionMetadata();

    // Pause current playing media
    if (currentPlayingRef.current && currentPlayingRef.current !== id) {
      pause(currentPlayingRef.current);
    }

    // Check if this is a video element
    const videoElement = videoElements.current[id];
    if (videoElement) {
      if (currentPlayingRef.current === id && !videoElement.paused) return;
      try {
        // If video has ended, reset to beginning
        if (
          videoElement.ended ||
          (videoElement.currentTime >= videoElement.duration &&
            videoElement.duration > 0)
        ) {
          videoElement.currentTime = 0;
        }

        await videoElement.play();

        currentPlayingRef.current = id;
        mediaStateStore.setState({
          currentId: id,
          status: "playing",
          progress: 0,
        });
      } catch (error) {
        // Handle AbortError gracefully - it's expected when play is interrupted
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        console.error("[MediaContext] Failed to play video for id:", id, error);
      }
      return;
    }

    // Ensure audio element and source exists
    const audioElement = audioElementRef.current;
    if (!audioElement) return;

    const blobUrl = audioSources.current[id];
    if (!blobUrl) return;
    try {
      // Set source and load
      audioElement.setAttribute("src", blobUrl);
      audioElement.load();

      await ensureAudioContextRunning();

      await audioElement.play();
      currentPlayingRef.current = id;
      mediaStateStore.setState({
        currentId: id,
        status: "playing",
        progress: 0,
      });
    } catch (error) {
      console.error(`[AudioProvider] Failed to play audio: ${id}`, error);
    }
  }, []);

  const pause = useCallback(
    (id: string) => {
      if (currentPlayingRef.current !== id) return;

      // Check if this is a video element
      const videoElement = videoElements.current[id];
      if (videoElement) {
        videoElement.pause();
        mediaStateStore.setState({
          currentId: id,
          status: "paused",
          progress: 0,
        });
        return;
      }

      // Handle audio pause
      const audioElement = audioElementRef.current;
      if (audioElement) {
        audioElement.pause();
        mediaStateStore.setState({
          currentId: id,
          status: "paused",
          progress: 0,
        });
      }
    },
    [mediaStateStore]
  );

  const setVolume = useCallback((volume: number) => {
    if (!gainNodeRef.current || !audioContextRef.current) return;

    // Map 0-100 to 0-10 gain (non-linear)
    let gain: number;
    if (volume <= 50) {
      gain = volume / 50; // 0-50% maps to 0-1 gain
    } else {
      gain = 1 + ((volume - 50) / 50) * 9; // 50-100% maps to 1-10 gain
    }

    gainNodeRef.current.gain.setValueAtTime(
      gain,
      audioContextRef.current.currentTime
    );
  }, []);

  // Apply volume from settings when settings change
  useEffect(() => {
    if (gainNodeRef.current && audioContextRef.current) {
      setVolume(settings.volume);
    }
  }, [settings.volume, setVolume]);

  // === GLOBAL VIDEO CONTROL METHODS ===
  const onVideoEnd = useCallback(
    (messageId: string) => {
      if (fullscreenVideoMessageId === messageId) {
        setFullscreenVideoMessageId(null);
      }

      // Reset currentPlayingRef if this was the currently playing video
      const videoId = messageId.startsWith("vid-")
        ? messageId
        : `vid-${messageId}`;
      if (currentPlayingRef.current === videoId) {
        currentPlayingRef.current = null;
        mediaStateStore.setState({
          currentId: null,
          status: "ended",
          progress: 0,
        });
      }
    },
    [fullscreenVideoMessageId, mediaStateStore]
  );

  // === VOICE RECORDING METHODS ===
  const initializeVoiceWorker = useCallback(async () => {
    if (workerRef.current) return;

    workerRef.current = new Worker(
      new URL("../workers/moonshine.worker.ts", import.meta.url),
      { type: "module" }
    );

    workerRef.current.addEventListener(
      "message",
      ({ data }: MessageEvent<VoiceMsg>) => {
        if (data.type === "error") {
          setError(data.message);
          return;
        }

        if (data.type === "status") {
          setStatus(data.status);
          if (data.status === "ready") setIsModelLoaded(true);
        }

        if (data.type === "output") {
          // Create a promise that resolves when the transcript is updated
          transcriptUpdatePromiseRef.current = new Promise<void>((resolve) => {
            setTranscript((prev) => {
              const newText = prev ? `${prev} ${data.message}` : data.message;

              // Truncate if too long
              if (newText.length > MAX_CHARACTERS) {
                const truncated = newText.slice(-MAX_CHARACTERS);
                const lastSpaceIndex = truncated.indexOf(" ");
                const result =
                  lastSpaceIndex > 0
                    ? truncated.slice(lastSpaceIndex + 1)
                    : truncated;
                // Resolve the promise after state update
                setTimeout(() => resolve(), 0);
                return result;
              }

              // Resolve the promise after state update
              setTimeout(() => resolve(), 0);
              return newText;
            });
          });
        }

        if (data.type === "command") {
          handleVoiceCommand(data.command);
        }
      }
    );

    workerRef.current.addEventListener("error", (e: ErrorEvent) => {
      setError(e.message);
    });
  }, []);

  const initializeVoice = useCallback(async () => {
    if (workerRef.current) return; // Already initialized

    await initializeVoiceWorker();
  }, [initializeVoiceWorker]);

  const startListening = useCallback(async () => {
    // Guard against multiple simultaneous calls
    if (isStartingListeningRef.current) return;
    if (isListening) return;
    isStartingListeningRef.current = true;
    if (!workerRef.current) {
      await initializeVoiceWorker();
    }

    try {
      // Ensure audio context is initialized
      if (!audioContextRef.current) {
        await initializeAudioContext();
      }

      if (isiOS() && navigator.audioSession) {
        // swtiching the audio session type from auto to playback-and-record
        // forces iOS to route to headphones if headphones are connected
        // (this fixed the bug where it was playing from speakers instead of headphones)
        navigator.audioSession.type = "auto";
      }

      // Get microphone stream
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          autoGainControl: false,
          noiseSuppression: false,
          sampleRate: SAMPLE_RATE,
        },
      });

      mediaStreamRef.current = stream;

      // Create media stream source
      mediaStreamSourceRef.current =
        audioContextRef.current!.createMediaStreamSource(stream);

      // Load audio worklet
      await audioContextRef.current!.audioWorklet.addModule(
        new URL("../../public/processor.worklet.js", import.meta.url)
      );

      // Create worklet node - ensure AudioContext is running
      await ensureAudioContextRunning();

      workletNodeRef.current = new AudioWorkletNode(
        audioContextRef.current!,
        "vad-processor",
        {
          numberOfInputs: 1,
          numberOfOutputs: 0,
          channelCount: 1,
          channelCountMode: "explicit",
        }
      );

      // Connect audio graph
      mediaStreamSourceRef.current.connect(workletNodeRef.current);

      // Handle worklet voiceMessages
      workletNodeRef.current.port.onmessage = (e) => {
        const buffer: Float32Array = e.data.buffer;
        workerRef.current!.postMessage({ buffer });
      };

      if (isiOS() && navigator.audioSession) {
        navigator.audioSession.type = "play-and-record";
      }

      // if needed for audio routing debugging:
      // - play silent audio
      // - ensure audio context is running again ensureAudioContextRunning()
      // - ensure media session API metadata is still set

      setIsListening(true);
    } catch (error) {
      setError("Failed to start microphone");
      if (error instanceof DOMException && error.name == "NotAllowedError") {
        cleanUpConversationMode();
      }
      console.error("[AudioProvider] Failed to start listening:", error);
    } finally {
      isStartingListeningRef.current = false;
    }
  }, [initializeVoiceWorker, initializeAudioContext, isListening]);

  const stopListening = useCallback(() => {
    if (mediaStreamSourceRef.current) {
      mediaStreamSourceRef.current.disconnect();
      mediaStreamSourceRef.current = null;
    }

    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    setIsListening(false);
    isStartingListeningRef.current = false;
  }, []);

  const clearTranscript = useCallback(() => {
    setTranscript("");
    // Clear any existing timer when manually clearing
    if (transcriptTimerRef.current) {
      clearTimeout(transcriptTimerRef.current);
      transcriptTimerRef.current = null;
    }
  }, []);

  // === TRANSCRIPT AUTO-CLEAR TIMER ===
  useEffect(() => {
    // Clear any existing timer
    if (transcriptTimerRef.current) {
      clearTimeout(transcriptTimerRef.current);
    }

    // Start new timer if there's a transcript
    if (transcript) {
      transcriptTimerRef.current = setTimeout(() => {
        setTranscript("");
        transcriptTimerRef.current = null;
      }, TRANSCRIPTION_CLEAR_TIMER_MS);
    }

    // Cleanup
    return () => {
      if (transcriptTimerRef.current) {
        clearTimeout(transcriptTimerRef.current);
        transcriptTimerRef.current = null;
      }
    };
  }, [transcript]);

  // === HANDLE COMPARE REQUEST HOOK ===
  const { handleCompareRequest } = useHandleCompareRequest({
    audio: { play, setFullscreenVideoMessageId },
  });

  const sendTranscript = useCallback(async () => {
    // Wait for any pending transcript updates to complete
    if (transcriptUpdatePromiseRef.current) {
      await transcriptUpdatePromiseRef.current;
    }

    const currentTranscript = transcriptRef.current;
    if (!currentTranscript.trim()) return;

    // Add STT prefix to voice command messages
    const messageWithSTTPrefix = `STT: ${currentTranscript}`;

    // Use the handleCompareRequest from the hook
    try {
      await handleCompareRequest(messageWithSTTPrefix);
    } catch (error) {
      console.error("Error in handleCompareRequest:", error);
    }
    setTranscript("");
  }, [handleCompareRequest]);

  const cleanUpConversationMode = useCallback(() => {
    setConversationMode(false);
    setPlayResponseOutloud(originalPlayResponseOutloud || false);

    // pause any currently playing media
    if (currentPlayingRef.current) {
      pause(currentPlayingRef.current);
      currentPlayingRef.current = null;
      mediaStateStore.setState({
        currentId: null,
        status: "paused",
        progress: 0,
      });
    }
    // Recreate AudioContext when disabling conversation mode (hopefully will fix audio volume/routing issues)
    if (isListening) stopListening();
    clearTranscript();
    destroyAudioContext();
  }, [
    pause,
    currentPlayingRef,
    mediaStateStore,
    isListening,
    stopListening,
    clearTranscript,
    destroyAudioContext,
    originalPlayResponseOutloud,
  ]);

  // === VOICE COMMANDS ===
  const handleVoiceCommand = useCallback(
    (command: string) => {
      switch (command.toLowerCase()) {
        case "exit handsfree mode":
        case "exit conversation mode":
        case "exit hands free mode":
        case "exit hands-free mode":
        case "stop listening":
          cleanUpConversationMode();
          break;
        case "send message":
        case "send a message":
          sendTranscript();
          break;
        case "clear message":
        case "clear a message":
        case "cancel message":
          clearTranscript();
          break;
        case "stop talking":
          if (currentPlayingRef.current) {
            pause(currentPlayingRef.current);
          }
          break;
        case "play video":
        case "play a video":
          if (currentPlayingRef.current) {
            pause(currentPlayingRef.current);
          }
          const videoMsgId = getLastVideoMessageId();
          if (videoMsgId) {
            play(
              videoMsgId.startsWith("vid-") ? videoMsgId : "vid-" + videoMsgId
            );
            setFullscreenVideoMessageId(videoMsgId);
          }
          break;
        case "pause video":
        case "pause a video":
          const videoId = getLastVideoMessageId();
          if (videoId) {
            pause(videoId.startsWith("vid-") ? videoId : "vid-" + videoId);
          }
          break;
        case "close video":
        case "close a video":
          if (currentPlayingRef.current) {
            pause(currentPlayingRef.current);
          }
          setFullscreenVideoMessageId(null);
          break;
      }
    },
    [pause, stopListening, clearTranscript, sendTranscript, mediaStateStore]
  );

  // === AUDIO TRUST INITIALIZATION ===
  const initializeAudioTrust = useCallback(async (): Promise<boolean> => {
    if (isChrome()) {
      setIsAudioTrustInitialized(true);
      return true;
    }

    const audioElement = audioElementRef.current;
    if (!audioElement) return false;

    if (isAudioTrustInitialized) return true;

    try {
      // Create silent audio blob
      const silentBlob = createSilentAudioBlob();
      const silentUrl = URL.createObjectURL(silentBlob);

      audioElement.src = silentUrl;
      audioElement.load();
      await audioElement.play();

      setTimeout(() => URL.revokeObjectURL(silentUrl), 1000);

      setIsAudioTrustInitialized(true);
      return true;
    } catch (error) {
      console.error(
        "[AudioProvider] Audio trust initialization failed:",
        error
      );
      return false;
    }
  }, [isAudioTrustInitialized]);

  // === USER GESTURE EVENT LISTENERS ===
  useEffect(() => {
    // Also listen for any click events on the document as a fallback
    const handleDocumentClick = (event: Event) => {
      const target = event.target as HTMLElement;
      if (!target) return;

      // Find the element with data-trust-trigger attribute (either target itself or closest parent)
      const trustTriggerElement = target.matches("[data-trust-trigger]")
        ? target
        : target.closest("[data-trust-trigger]");

      if (
        trustTriggerElement &&
        (trustTriggerElement.matches('[data-trust-trigger="mic-button"]') ||
          playResponseOutloud ||
          trustTriggerElement.matches(
            '[data-trust-trigger="play-responses"]'
          ) ||
          trustTriggerElement.matches('[data-trust-trigger="audio-control"]') ||
          trustTriggerElement.matches('[data-trust-trigger="media-display"]'))
      ) {
        initializeAudioContext().catch((error) => {
          console.error(
            "[AudioProvider] Failed to initialize audio context on click:",
            error
          );
        });
      }
    };

    document.addEventListener("click", handleDocumentClick);

    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [initializeAudioContext, playResponseOutloud]);

  // === PAGE VISIBILITY AND LIFECYCLE HANDLING ===
  // This effect handles page visibility changes, browser close events, and mobile app state changes
  // to ensure audio is paused and media session is disconnected when the user:
  // - Locks their screen
  // - Switches to another app/tab
  // - Closes the browser
  // - Minimizes the browser
  useEffect(() => {
    const handlePageClose = () => {
      // Pause any audio/video, stop mic input, and disconnection media session API
      if (currentPlayingRef.current) pause(currentPlayingRef.current);
      if (isListening) stopListening();
      disconnectMediaSession();
      setConversationMode(false);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        handlePageClose();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handlePageClose);
    window.addEventListener("pagehide", handlePageClose);
    document.addEventListener("freeze", handlePageClose); // Page frozen (mobile)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handlePageClose);
      window.removeEventListener("pagehide", handlePageClose);
      document.removeEventListener("freeze", handlePageClose);
    };
  }, [pause, isListening, stopListening]);

  // === CLEAN UP ===
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }

      // Clean up blob URLs
      Object.values(audioSources.current).forEach((url) => {
        URL.revokeObjectURL(url);
      });

      // Clean up worker
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
  }, []);

  // === PROVIDER VALUE ===
  const value: AudioProvider = {
    // Media Playback
    registerAudioSource,
    unregisterAudioSource,
    play,
    pause,
    setVolume,

    // Voice Recording
    initializeVoice,
    startListening,
    stopListening,
    isListening,
    transcript,
    clearTranscript,
    sendTranscript,

    // Conversation Mode
    conversationMode,
    setConversationMode: handleConversationModeToggle,

    // Play Responses Out Loud
    playResponseOutloud,
    setPlayResponseOutloud,

    // Audio Trust & iOS
    initializeAudioTrust,
    isAudioTrustInitialized,

    isModelLoaded,
    status,
    error,

    // Media State Store
    mediaState: mediaStateStore,

    // Video methods
    registerVideoSource,
    unregisterVideoSource,

    // Global video control
    fullscreenVideoMessageId,
    setFullscreenVideoMessageId,
    onVideoEnd,
  };

  return (
    <AudioContext.Provider value={value}>
      {/* Hidden shared audio element */}
      <audio
        key={audioKey}
        ref={audioElementRef}
        style={{ display: "none" }}
        preload={isiOS() ? "auto" : "none"}
        crossOrigin="anonymous"
        onEnded={() => {
          currentPlayingRef.current = null;
          mediaStateStore.setState({
            currentId: null,
            status: "ended",
            progress: 0,
          });
        }}
      />
      {children}
    </AudioContext.Provider>
  );
};
